import React from "react";

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Separator } from "../separator";
import { ScrollArea } from "../library/scroll-area";
import { ChevronLeft } from "lucide-react";
import { useBreakpoint } from "@/hooks/useViewport";

type ResponsiveWidths = {
  mobile?: string;
  tablet?: string;
  tabletLg?: string;
  desktop?: string;
};

const DEFAULT_WIDTHS: Required<ResponsiveWidths> = {
  mobile: "100vw",
  tablet: "70vw",
  tabletLg: "55vw",
  desktop: "40vw",
};

/**
 * Width preset for filter/detail sheets — narrower than form drawers at
 * desktop (since filter content is compact pills/dropdowns) but wide enough
 * on tablet to avoid label truncation. Spread into a sheet's `widths` prop:
 *
 *   <RightSideSheet widths={FILTER_SHEET_WIDTHS} ... />
 */
export const FILTER_SHEET_WIDTHS: ResponsiveWidths = {
  mobile: "100vw",
  tablet: "70vw",
  tabletLg: "50vw",
  desktop: "30vw",
};

interface RightSideSheetProps {
  trigger?: React.ReactNode;
  title?: string;
  description?: string;
  children: React.ReactNode;
  actions?: React.ReactNode;
  /** Optional action node rendered in the header row (left of the close X). */
  headerActions?: React.ReactNode;
  /**
   * Optional overlay rendered as a sibling of the scroll area, inside the Sheet's
   * dialog content. Use this for modals/panels that need to escape the ScrollArea's
   * transform (so `position: fixed` works) while still being part of the dialog
   * tree (so Radix doesn't treat clicks on it as outside the dialog).
   */
  overlay?: React.ReactNode;
  open: boolean;
  handleClose: any;
  /** Static override. When provided, used at all breakpoints. */
  width?: string;
  /** Per-breakpoint widths. Merged with defaults. Ignored if `width` is set. */
  widths?: ResponsiveWidths;
  scrollAreaColor?: string;
  handleBack?: () => void;
  backIcon?: boolean;
  showHeader?: boolean;
  showCloseButton?: boolean;
}

export default function RightSideSheet({
  trigger,
  title = "Panel Title",
  description = "",
  children,
  actions,
  headerActions,
  overlay,
  handleClose,
  open,
  width,
  widths,
  scrollAreaColor = "#F8F8F8",
  handleBack,
  backIcon = false,
  showHeader = true,
  showCloseButton = true,
}: RightSideSheetProps) {
  const breakpoint = useBreakpoint();

  const resolvedWidth = React.useMemo(() => {
    if (width) return width;
    const merged = { ...DEFAULT_WIDTHS, ...(widths ?? {}) };
    switch (breakpoint) {
      case "mobile":
        return merged.mobile;
      case "tablet":
        return merged.tablet;
      case "tablet-lg":
        return merged.tabletLg;
      case "desktop":
      default:
        return merged.desktop;
    }
  }, [breakpoint, width, widths]);

  return (
    <>
      {trigger && trigger}
      <Sheet open={open} onOpenChange={handleClose}>
        <SheetContent
          style={{ width: resolvedWidth }}
          side="right"
          className="bg-transparent border-none shadow-none overflow-visible sheet"
          showCloseButton={showCloseButton}
        >
          <div className="my-2 h-full rounded-3xl bg-white shadow-[0_8px_40px_rgba(15,23,42,0.1)] sheet-content">
            {showHeader && (
              <>
                <SheetHeader className="sheet-content-header">
                  <div className="flex items-center justify-between gap-2">
                    <SheetTitle asChild>
                      <div className="flex items-center gap-2">
                        {backIcon && (
                          <ChevronLeft
                            className="size-5 cursor-pointer"
                            onClick={handleBack}
                          />
                        )}
                        {title}
                      </div>
                    </SheetTitle>
                    {headerActions && (
                      <div className="flex items-center gap-2 pr-8">
                        {headerActions}
                      </div>
                    )}
                  </div>
                  <SheetDescription
                    className="text-b3"
                    style={{ color: "var(--color-black-6)" }}
                  >
                    {description}
                  </SheetDescription>
                </SheetHeader>
                <Separator />
              </>
            )}
            <ScrollArea
              // Radix wraps the viewport's children in `display: table;
              // min-width: 100%`. A table sizes to its content's MIN-CONTENT
              // width, and a `truncate` (nowrap) label contributes its full
              // text width to that — so one long label (a custom-field name
              // on the Edit Role → Permission step) made the whole body wider
              // than the sheet and the right column (switches, Allow/Deny
              // all, the filter dropdown) was clipped. The sheet never scrolls
              // sideways (vertical bar only), so forcing the wrapper to a
              // full-width block costs nothing and gives flex rows the real
              // width to truncate against.
              className="h-full [&_[data-slot=scroll-area-viewport]>div]:block! [&_[data-slot=scroll-area-viewport]>div]:w-full"
              style={{
                background: scrollAreaColor,
              }}
            >
              <div className="sheet-content-body">{children}</div>
            </ScrollArea>
            {actions && <div className="sheet-footer">{actions}</div>}
          </div>
          {overlay}
        </SheetContent>
      </Sheet>
    </>
  );
}
